import React, { useState, useEffect } from 'react'
import { api, formatCurrency } from '../../utils/api'
import { SHOPIFY_ADMIN_URL } from '../../utils/constants'

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live' },
  { value: 'draft', label: 'Draft' }
]

function StaffPicks() {
  const [picks, setPicks] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [editingId, setEditingId] = useState(null)

  useEffect(() => { loadPicks() }, [filter])

  async function loadPicks() {
    setLoading(true)
    try {
      const query = filter === 'all' ? '' : `?status=${filter}`
      const res = await api.get(`/api/marketing/staff-picks${query}`)
      setPicks(res.picks || [])
    } catch (error) {
      console.error('Failed to load staff picks:', error)
    } finally {
      setLoading(false)
    }
  }

  async function patchPick(id, body) {
    try {
      await api.patch(`/api/marketing/staff-picks/${id}`, body)
      await loadPicks()
    } catch (error) {
      console.error('Failed to update staff pick:', error)
      alert('Update failed. Please try again.')
    }
  }

  async function deletePick(id) {
    if (!confirm('Remove this staff pick? It will no longer appear on the site.')) return
    try {
      await api.delete(`/api/marketing/staff-picks/${id}`)
      await loadPicks()
    } catch (error) {
      console.error('Failed to delete staff pick:', error)
    }
  }

  function toggleStatus(pick) {
    patchPick(pick.id, { status: pick.status === 'live' ? 'draft' : 'live' })
  }

  if (loading) return <div className="text-sm text-muted" style={{ padding: '16px 0' }}>Loading…</div>

  return (
    <div>
      <div className="filter-section">
        <div className="filter-group">
          <label className="filter-label">Status</label>
          <div className="pageTabs" style={{ marginTop: 0 }}>
            {STATUS_FILTERS.map(f => (
              <a
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={filter === f.value ? 'active' : ''}
                style={{ cursor: 'pointer' }}
              >
                {f.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {picks.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">No staff picks yet</div>
          <div className="empty-state-description">
            Flag products with the STAFF PICK button in Receiving or Inventory. They land here as Draft, then you set them Live.
          </div>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th></th>
              <th>Product</th>
              <th>Price</th>
              <th>Priority</th>
              <th>On site</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {picks.map(pick => (
              <PickRow
                key={pick.id}
                pick={pick}
                expanded={editingId === pick.id}
                onToggleExpand={() => setEditingId(editingId === pick.id ? null : pick.id)}
                onToggleStatus={() => toggleStatus(pick)}
                onPatch={patchPick}
                onDelete={() => deletePick(pick.id)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function PickRow({ pick, expanded, onToggleExpand, onToggleStatus, onPatch, onDelete }) {
  const [priority, setPriority] = useState(pick.priority)
  const [blurb, setBlurb] = useState(pick.blurb || '')
  const [imageUrl, setImageUrl] = useState(pick.image_url || '')
  const isLive = pick.status === 'live'

  function savePriority() {
    if (Number(priority) !== pick.priority) onPatch(pick.id, { priority: Number(priority) })
  }

  function saveOverrides() {
    onPatch(pick.id, { override_blurb: blurb, override_image_url: imageUrl })
    onToggleExpand()
  }

  return (
    <>
      <tr>
        <td>
          {pick.image_url
            ? <img src={pick.image_url} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
            : <div style={{ width: 48, height: 48, background: '#f1f1f1', borderRadius: 6 }} />}
        </td>
        <td>
          <div>{pick.title || 'Untitled product'}</div>
          <a className="text-sm text-muted" href={`${SHOPIFY_ADMIN_URL}/products/${pick.shopify_product_id}`} target="_blank" rel="noreferrer">
            #{pick.shopify_product_id}
          </a>
        </td>
        <td>{pick.price_cents != null ? formatCurrency(pick.price_cents / 100) : '-'}</td>
        <td>
          <input
            className="form-input"
            type="number"
            value={priority}
            onChange={e => setPriority(e.target.value)}
            onBlur={savePriority}
            style={{ width: 70 }}
            title="Lower number = higher priority"
          />
        </td>
        <td>
          {isLive
            ? <span className="badge badge-success">On site</span>
            : <span className="text-sm text-muted">Hidden</span>}
        </td>
        <td>
          <button className={`btn ${isLive ? 'btn-secondary' : 'btn-success'}`} onClick={onToggleStatus}>
            {isLive ? 'Set Draft' : 'Set Live'}
          </button>
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          <button className="btn btn-secondary" onClick={onToggleExpand}>{expanded ? 'Close' : 'Overrides'}</button>{' '}
          <button className="btn btn-secondary" onClick={onDelete}>Remove</button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7}>
            <div className="card" style={{ padding: 16 }}>
              <div className="filter-group filter-group-wide">
                <label className="filter-label">Override blurb (optional - defaults to the product card blurb)</label>
                <input className="form-input" value={blurb} onChange={e => setBlurb(e.target.value)} maxLength={300} placeholder="Short spotlight line" />
              </div>
              <div className="filter-group filter-group-wide" style={{ marginTop: 12 }}>
                <label className="filter-label">Override image URL (optional - defaults to the product image)</label>
                <input className="form-input" value={imageUrl} onChange={e => setImageUrl(e.target.value)} maxLength={600} placeholder="https://..." />
              </div>
              <div className="filter-actions" style={{ marginTop: 12 }}>
                <button className="btn btn-primary" onClick={saveOverrides}>Save overrides</button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

export default StaffPicks
